import { type StateCreatorEnhance } from '@halo/zustand-enhance';
import type { MediaSDKOptions, MediaType, User } from '@halo/chat-sdk';
import {
  CallStatus,
  isSessionCategory,
  MediaAction,
  MediaSDK,
  SessionCategoryPrefix,
  MediaStatus,
} from '@halo/chat-sdk';
import { type MediaCallOptions } from '@halo/chat-sdk';
import type { AppStore } from '../main';
import { createMediaStore, type MediaStoreInstance } from './main';

export type CreateMediasOptions = Omit<MediaSDKOptions, 'appid' | 'url' | 'debug'> & {
  debugLevel: number;
  media_appid: string;
  media_ws_url: string;
};

export type InitMediasOptions = unknown;

export interface MediasStore {
  media: MediaSDK;
  mediaActivatedId: string | undefined;
  mediaWaitCalleeId: string | undefined;
  mediaStores: Record<string, MediaStoreInstance>;
  hasOtherDeviceJoinCall: boolean;
  userRoomInfo: {
    open: boolean;
    member: any;
  };
  setUserRoomInfo: (options: { open: boolean; member: any }) => void;
  createMedias: (options: CreateMediasOptions) => void;
  initMedias: () => void;
  createMedia: (options: {
    session_id: string;
    caller?: User;
    room_id?: string;
    type?: MediaType;
  }) => MediaStoreInstance | undefined;
  mediaActive: (session_id: string) => Promise<void>;
  mediaCall: (
    options: Pick<MediaCallOptions, 'session_id' | 'type' | 'membersId'>
  ) => Promise<void>;
  mediaJoin: (options: { session_id: string; joinCallType?: MediaType }) => Promise<void>;
  mediaCallee: (options: {
    session_id: string;
    room_id: string;
    type: MediaType;
  }) => Promise<void>;
  mediaClean: (options: { session_id: string }) => Promise<void>;
  disposeMedias: () => void;
}

export const mediasStoreCreator: StateCreatorEnhance<AppStore> = (set, get, instance) =>
  ({
    media: undefined,
    mediaActivatedId: undefined,
    mediaWaitCalleeId: undefined,
    mediaStores: {},
    hasOtherDeviceJoinCall: false,
    userRoomInfo: {
      open: false,
      member: undefined,
    },
    setUserRoomInfo: (options) => set({ userRoomInfo: options }),
    createMedias: ({ media_appid, media_ws_url, debugLevel, ...options }) => {
      set({
        media: new MediaSDK({
          ...options,
          debug: Boolean(debugLevel),
          appid: media_appid,
          url: media_ws_url,
        }),
      });
    },
    initMedias: () => {
      const { deviceId, user, media, client, activeSession, effects } = get();

      effects.mediasMediaCall = client.addEventListener('MediaCall', ({ detail: data }) => {
        const { mediaCallee, createMedia, mediaActivatedId, mediaWaitCalleeId, mediaStores } =
          get();
        if (mediaActivatedId) {
          const mediaStore = mediaStores[mediaActivatedId];
          if (mediaStore && mediaStore.getState().ringingCancel) {
            mediaStore.getState().operate({
              action: MediaAction.CALL_CANCELLED,
            });
          }

          if (
            mediaActivatedId !== data.chat_id &&
            data.sender_id !== user.user_id &&
            !mediaWaitCalleeId
          ) {
            mediaCallee({
              session_id: data.chat_id,
              room_id: data.room_id,
              type: data.media_type,
            });
            set({ mediaWaitCalleeId: data.chat_id });
          }
        } else if (data.sender_id !== user.user_id) {
          activeSession(data.chat_id);
          mediaCallee({ session_id: data.chat_id, room_id: data.room_id, type: data.media_type });
          set({ mediaActivatedId: data.chat_id });
        } else if (data.sender_id === user.user_id) {
          const mediaStore = createMedia({ session_id: data.chat_id, room_id: data.room_id });
          if (mediaStore) {
            mediaStore.getState().syncInfo();
          }
        }
      });

      effects.mediaCallStatusChanged = client.addEventListener(
        'MediaCallStatusChanged',
        async ({ detail }) => {
          const { ope_type, chat_id, room_id } = detail;
          const { mediaStores, createMedia, activatedSessionId } = get();
          const mediaStore = mediaStores[chat_id];
          if (
            isSessionCategory(chat_id, SessionCategoryPrefix.Group) &&
            ope_type === MediaAction.CALL_SYNC_STATUS &&
            chat_id === activatedSessionId &&
            !mediaStore
          ) {
            const mediaStore = createMedia({ session_id: chat_id, room_id });
            if (mediaStore) {
              mediaStore.getState().syncInfo();
            }
          }
        }
      );

      client.Api.media.mediaCallDrop();

      //getUserMediaInfo
      client.Api.media.getUserMediaInfo().then((detail) => {
        const { chat_id, room_id } = detail;
        const { mediaStores, createMedia } = get();
        const mediaStore = mediaStores[chat_id];
        if (isSessionCategory(chat_id, SessionCategoryPrefix.Single) && !mediaStore) {
          const mediaStore = createMedia({ session_id: chat_id, room_id });
          if (mediaStore) {
            mediaStore.getState().syncInfo();
          }
        }
      });

      media.init({ userId: user.user_id, deviceId });

      // TODO amo: No removeNotify so only addNotify here
      media.quickvo.addNotify({
        event: 'onRoomUsers',
        callback: async (res) => {
          media.logger.log('event:onRoomUsers', res);
          const { mediaActivatedId, mediaStores } = get();
          const mediaStore = mediaStores[mediaActivatedId];
          if (mediaStore) {
            mediaStore.getState().updateMembers(
              res.data.map((member) => ({
                user_id: member.id,
                ...member,
              }))
            );
          }
        },
      });

      media.quickvo.addNotify({
        event: 'onRoomUsersStreams',
        callback: async (res) => {
          media.logger.log('event:onRoomUsersStreams', res);
          const { mediaActivatedId, mediaStores } = get();
          const mediaStore = mediaStores[mediaActivatedId];
          if (mediaStore) {
            mediaStore.getState().updateMembersStreams(
              res.data.map(({ userId, ...member }) => ({
                ...member,
                user_id: userId,
              }))
            );
          }
        },
      });

      media.quickvo.addNotify({
        event: 'onJoinRoom',
        callback: async (res) => {
          console.log('event:onJoinRoom', res);
        },
      });

      media.quickvo.addNotify({
        event: 'onQuitRoom',
        callback: async (res) => {
          console.log('event:onQuitRoom', res);
        },
      });

      media.quickvo.addNotify({
        event: 'onDevicechange',
        callback: async (res) => {
          media.logger.log('event:onDevicechange', res);
          const { mediaActivatedId, mediaStores } = get();
          const mediaStore = mediaStores[mediaActivatedId];
          if (mediaStore) {
            mediaStore.getState().updateDevices();
          }
        },
      });

      media.quickvo.addNotify({
        event: 'onRoomState',
        callback: async (res) => {
          const { state } = res.data;
          const { mediaActivatedId, mediaStores, mediaClean } = get();
          const mediaStore = mediaStores[mediaActivatedId];
          const { status } = mediaStore?.getState() || {};

          if (mediaStore && status === MediaStatus.JOIN) {
            if (state === 'connecting') {
              mediaStore.setState({ callStatus: CallStatus.NETWORK_RECONNECTING });
            } else if (state === 'closed') {
              mediaStore.setState({ callStatus: CallStatus.NETWORK_ERROR });
              mediaClean({ session_id: mediaActivatedId });
            } else {
              mediaStore.setState({ callStatus: CallStatus.CONNECT });
            }
          }
        },
      });

      media.quickvo.addNotify({
        sn: 'onRoomNetwork',
        event: 'onRoomNetwork',
        callback: async (res) => {
          const { mediaActivatedId, mediaStores, userRoomInfo } = get();
          const mediaStore = mediaStores[mediaActivatedId];
          if (mediaStore && userRoomInfo.open) {
            const reports = await mediaStore
              .getState()
              .getReportsByMid()
              .catch(() => []);
            mediaStore.setState({ roomNetwork: res?.data || {}, reports });
          }
        },
      });

      instance.subscribe(
        (store) => store.mediaStores,
        (mediaStores) => {
          const hasOtherDeviceJoinCall = !!Object.values(mediaStores).find(
            (mediaStore) => mediaStore.getState().isOtherDeviceJoinCall
          );

          set({ hasOtherDeviceJoinCall });
        }
      );
    },
    createMedia: ({ session_id, caller, room_id, type }) => {
      const { client, sessions, mediaStores, user, media, mediaClean, deviceId } = get();
      const session = sessions.find((s) => s.chat_id === session_id);
      if (session) {
        let mediaStore = mediaStores[session.chat_id];
        if (!mediaStore) {
          mediaStore = createMediaStore({
            media,
            client,
            session,
            user,
            caller,
            deviceId,
            room_id,
            type,
          });

          mediaStore.subscribe(
            (store) => store.status,
            async (status) => {
              const { mediaActivatedId, mediaWaitCalleeId } = get();
              if (status === undefined) {
                mediaClean({ session_id: session.chat_id });
              } else if (status === MediaStatus.CREATED) {
                Object.values(mediaStore.getState().devicesEffects).forEach((handler) =>
                  handler()
                );
                if (mediaActivatedId === session.chat_id) {
                  set({ mediaActivatedId: undefined });
                  await media.quickvo.quitRoomEx().catch((_) => null);
                  // sdk quitRoom is async
                  await new Promise((resolve) => setTimeout(resolve, 1000));
                  mediaStore.getState().hangupTrigger?.resolve(true);
                } else if (mediaWaitCalleeId) {
                  set({ mediaWaitCalleeId: undefined });
                }
              }
            }
          );

          mediaStore.subscribe(
            (store) => store.isOtherDeviceJoinCall,
            () => {
              const { mediaStores } = get();
              const hasOtherDeviceJoinCall = !!Object.values(mediaStores).find(
                (mediaStore) => mediaStore.getState().isOtherDeviceJoinCall
              );
              set({ hasOtherDeviceJoinCall });
            }
          );

          set((store) => {
            const { mediaStores } = store;
            mediaStores[session.chat_id] = mediaStore;
          });
        }
        return mediaStore;
      }
    },
    mediaActive: async (session_id) => {
      const { createMedia, client } = get();
      if (!isSessionCategory(session_id, SessionCategoryPrefix.Group)) {
        return;
      }

      const groupMediaInfo = await client.Api.media.getGroupMediaInfo(session_id);
      if (groupMediaInfo?.create_uid) {
        const mediaStore = createMedia({ session_id, room_id: groupMediaInfo.room_id });
        if (mediaStore) {
          mediaStore.getState().syncInfo();
        }
      }
    },
    mediaCall: async (options) => {
      const { createMedia, user } = get();
      const mediaStore = createMedia({ ...options, caller: user });
      if (mediaStore) {
        set({ mediaActivatedId: options.session_id });
        await mediaStore.getState().call(options);
      }
    },
    mediaJoin: async (options) => {
      const { mediaStores } = get();
      const mediaStore = mediaStores[options.session_id];
      if (mediaStore) {
        set({ mediaActivatedId: options.session_id });
        await mediaStore.getState().join(options);
      }
    },
    mediaCallee: (options) => {
      const { createMedia } = get();
      const mediaStore = createMedia(options);
      if (mediaStore) {
        mediaStore.getState().callee(options);
      }
    },
    mediaClean: async ({ session_id }) => {
      const { mediaStores, mediaActivatedId, mediaWaitCalleeId, media } = get();
      const mediaStore = mediaStores[session_id];
      if (mediaStore) {
        mediaStore.getState().destroy();

        if (mediaActivatedId === session_id) {
          set({ mediaActivatedId: undefined });
          await media.quickvo.quitRoomEx().catch((_) => null);
          await new Promise((resolve) => setTimeout(resolve, 1000));
          mediaStore.getState().hangupTrigger?.resolve(true);
        }

        if (mediaWaitCalleeId === session_id) {
          set({ mediaWaitCalleeId: undefined });
        }
        set((store) => {
          const { mediaStores } = store;
          delete mediaStores[session_id];
        });
      }
    },
    disposeMedias: () => {
      const { effects, media, mediaStores } = get();
      effects.mediasMediaCall?.();
      media?.[Symbol.dispose]();
      Object.values(mediaStores).forEach((mediaStore) => {
        mediaStore.getState().destroy();
      });
      set(instance.getInitialState());
    },
  }) as AppStore;
